/** Rolling last-10s replay from complete MediaRecorder sessions. */

/**
 * Any browser on iPhone/iPad (Safari, Chrome CriOS, Firefox FxiOS, Edge).
 * They all use WebKit — desktop Chrome's parallel MediaRecorder path does not
 * apply, and a second canvas encoder overheats the phone.
 */
export function isIosReplayClient() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/CriOS|FxiOS|EdgiOS/i.test(ua)) return true;
  return (
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1)
  );
}

/** Android Chrome / WebView. Canvas+MP4 there is slow and often a black file. */
export function isAndroidReplayUserAgent(ua) {
  const text = String(ua || "");
  if (/iPad|iPhone|iPod/i.test(text)) return false;
  return /Android/i.test(text);
}

export function isAndroidReplayClient() {
  if (typeof navigator === "undefined") return false;
  if (isIosReplayClient()) return false;
  return isAndroidReplayUserAgent(navigator.userAgent || "");
}

export const TABLE_REPLAY_WINDOW_SEC = 10;
export const TABLE_REPLAY_WINDOW_MS = TABLE_REPLAY_WINDOW_SEC * 1000;
/** Keep slightly more than the window so a recycle still covers 10s of wall time. */
export const TABLE_REPLAY_RETAIN_MS = 12_000;
/**
 * Recycle the MediaRecorder this often. WebM timeslices cannot be pruned mid-stream
 * and still play — a short complete session is remuxed/trimmed server-side instead.
 */
export const TABLE_REPLAY_RING_MAX_MS = 12_000;
/** Timeslice for the live ring — each chunk is kept with its end timestamp. */
export const TABLE_REPLAY_TIMESLICE_MS = 1_000;
export const TABLE_REPLAY_SEGMENT_MS = 2_000;
/**
 * Legacy Android defaults (480p / 30 / low). Live encode size now comes from
 * replayEncodeSettings — kept for tests and older call sites.
 */
export const TABLE_REPLAY_ANDROID_WIDTH = 854;
export const TABLE_REPLAY_ANDROID_HEIGHT = 480;
export const TABLE_REPLAY_ANDROID_FPS = 30;
export const TABLE_REPLAY_ANDROID_BITRATE = 450_000;
/**
 * A session younger than this is not worth stopping. The previous finalized
 * file is already a full clip and can upload immediately.
 */
export const TABLE_REPLAY_ANDROID_SHIP_SEALED_UNDER_MS = 2_500;

export function shouldShipSealedAndroidReplay(
  sessionMs,
  sealedSize,
  minBytes = 8192
) {
  return (
    Number(sealedSize) >= minBytes &&
    Number(sessionMs) >= 0 &&
    Number(sessionMs) < TABLE_REPLAY_ANDROID_SHIP_SEALED_UNDER_MS
  );
}

export function pruneReplaySegments(segments, now = Date.now()) {
  const cutoff = now - TABLE_REPLAY_RETAIN_MS;
  return (segments || []).filter(
    (segment) =>
      segment &&
      Number(segment.endedAt) >= cutoff &&
      segment.blob &&
      segment.blob.size > 0
  );
}

export function bufferedReplayMs(segments, now = Date.now()) {
  const list = segments || [];
  if (!list.length) return 0;
  // Prefer wall-clock span of the continuous session (valid WebM from start).
  const ended = list.map((s) => Number(s.endedAt)).filter(Number.isFinite);
  if (ended.length >= 2) {
    const span = Math.max(...ended) - Math.min(...ended) + TABLE_REPLAY_TIMESLICE_MS;
    if (span > 0) return Math.min(span, TABLE_REPLAY_RETAIN_MS);
  }
  return list.reduce(
    (sum, segment) => sum + (Number(segment.durationMs) || 0),
    0
  );
}

/**
 * Chunks to export for a finalized recorder session.
 * Always keeps the recorder's first chunk (init segment) and every following
 * chunk from this session — do not splice mid-WebM without remuxing.
 */
export function segmentsForExport(
  segments,
  {
    now = Date.now(),
    windowMs = TABLE_REPLAY_WINDOW_MS,
    initChunk = null,
    contiguous = true,
  } = {}
) {
  const list = (segments || []).filter(
    (segment) => segment && segment.blob && segment.blob.size > 0
  );
  if (!list.length && !initChunk) return [];

  // Contiguous session blob (after MediaRecorder.stop) is always preferred.
  if (contiguous) {
    if (!initChunk) return list;
    const initAlready = list.length && list[0].blob === initChunk.blob;
    if (initAlready) return list;
    return [
      {
        blob: initChunk.blob,
        endedAt: initChunk.endedAt || list[0]?.endedAt || now,
        durationMs: 0,
        isInit: true,
      },
      ...list,
    ];
  }

  const cutoff = now - Math.max(windowMs, TABLE_REPLAY_RETAIN_MS);
  const kept = list.filter((segment) => Number(segment.endedAt) >= cutoff);
  if (!kept.length && !initChunk) return [];
  if (!initChunk) return kept;
  const initAlready = kept.length && kept[0].blob === initChunk.blob;
  if (initAlready) return kept;
  return [
    {
      blob: initChunk.blob,
      endedAt: initChunk.endedAt || kept[0]?.endedAt || now,
      durationMs: 0,
      isInit: true,
    },
    ...kept,
  ];
}

export function blobFromReplaySegments(segments, mimeType) {
  const list = (segments || [])
    .map((segment) => segment?.blob)
    .filter((blob) => blob && blob.size > 0);
  if (!list.length) return null;
  const type =
    mimeType ||
    list.find((blob) => blob.type)?.type ||
    "video/webm";
  return new Blob(list, { type });
}
